import React from 'react';
import produce from 'immer';
import { streamChat, createChat } from './catalystApi';
import { fetchExpiredCaches } from '../lib/cacheExpired';
import { ReservoirChatContext } from './ReservoirChatContext';
import { UserContext } from '../App';
import {
  RESERVOIR_CHAT_PREFIX, CHAT_KIND_BUILDER,
  CATALYST_PROFILE_ID, CATALYST_BUILDER_PROFILE_ID,
  REPORT_BUILDER_SECURITY_GUID,
} from './constants';

// Local, AI-SDK-free chat hook. Messages are rich objects with `parts`:
//   { role, parts: [ {type:'text', text} | {type:'tool', toolCallId, toolName, args, result, state} ] }
// Outgoing API messages are the flattened text turns; tool parts render inline
// but aren't replayed to the model (Catalyst gets the full text history each turn).
export function useCatalystChat() {
  const [messages, setMessages] = React.useState([]);
  const [status, setStatus] = React.useState('idle'); // 'idle' | 'streaming'
  const [error, setError] = React.useState(null);
  // True when the report's pinned dataset cache was reaped (clearCaches.php): the chat would query a
  // different snapshot than the report shows, so the panel prompts a reload instead of sending.
  const [cacheExpired, setCacheExpired] = React.useState(false);
  const [chatId, setChatId] = React.useState(null); // exposed for insight provenance
  const abortRef = React.useRef(null);
  const chatIdRef = React.useRef(null); // persisted chat id for this conversation
  const messagesRef = React.useRef(messages);
  React.useEffect(() => { messagesRef.current = messages; }, [messages]);

  const { context } = React.useContext(ReservoirChatContext);
  const contextRef = React.useRef(context);
  React.useEffect(() => { contextRef.current = context; }, [context]);

  const userContext = React.useContext(UserContext);
  const hasBuilderAccess = userContext?.securities?.some(s => s.ua_fid === REPORT_BUILDER_SECURITY_GUID) ?? false;
  const hasBuilderAccessRef = React.useRef(hasBuilderAccess);
  React.useEffect(() => { hasBuilderAccessRef.current = hasBuilderAccess; }, [hasBuilderAccess]);

  // The Catalyst profile this chat resolves to, derived reactively from the page
  // context (builder vs report view). Exposed so the panel can show which skills
  // are attached to the active profile. Kept in lockstep with the send-time
  // derivation below (contextRef).
  const activeProfileId = context?.kind === CHAT_KIND_BUILDER && hasBuilderAccess
    ? CATALYST_BUILDER_PROFILE_ID
    : CATALYST_PROFILE_ID;

  const clearCacheExpired = React.useCallback(() => setCacheExpired(false), []);

  const sendMessage = React.useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || abortRef.current) return;
    setError(null);

    // Pre-flight: the chat pins to the report's dataset caches (forwarded to the MCP). If the cleanup
    // job reaped one, the MCP would silently answer against a different snapshot — so detect it and
    // prompt a reload instead of sending. Fail open: never block chat on a check failure.
    const caches = contextRef.current?.datasetCaches;
    const tables = caches ? Object.values(caches).filter(Boolean) : [];
    if (tables.length > 0) {
      // fetchExpiredCaches fails open (resolves [] on error), so a check failure never blocks chat.
      const expired = await fetchExpiredCaches(tables);
      if (expired.length > 0) {
        setCacheExpired(true);
        return;
      }
    }

    const toApi = (m) => ({
      role: m.role,
      content: m.parts.filter((p) => p.type === 'text').map((p) => p.text).join(''),
    });
    const prior = messagesRef.current.map(toApi).filter((m) => m.content);
    const outgoing = [...prior, { role: 'user', content: trimmed }];

    setMessages((prev) => [
      ...prev,
      { role: 'user', parts: [{ type: 'text', text: trimmed }] },
      { role: 'assistant', parts: [] },
    ]);
    setStatus('streaming');

    // Ensure a persisted chat exists so this turn is logged + attributed to the
    // user. Best-effort: if creation fails, continue unlogged rather than block chat.
    if (!chatIdRef.current) {
      try {
        chatIdRef.current = await createChat(RESERVOIR_CHAT_PREFIX + trimmed.slice(0, 80));
        setChatId(chatIdRef.current || null);
      } catch { /* proceed unlogged */ }
    }

    const ac = new AbortController();
    abortRef.current = ac;

    const patchLast = (recipe) => setMessages(produce((d) => { recipe(d[d.length - 1]); }));
    const ensureText = (m) => {
      let p = m.parts[m.parts.length - 1];
      if (!p || p.type !== 'text') { p = { type: 'text', text: '' }; m.parts.push(p); }
      return p;
    };

    // Route builder chats to the builder-aware profile so the model receives the working
    // definition + available fields and edits them via propose_report_edit.
    // Only users with report builder security get the builder profile.
    const profileId = contextRef.current?.kind === CHAT_KIND_BUILDER && hasBuilderAccessRef.current
      ? CATALYST_BUILDER_PROFILE_ID
      : CATALYST_PROFILE_ID;

    try {
      await streamChat(
        { messages: outgoing, chatId: chatIdRef.current || undefined, context: contextRef.current || undefined, profileId, signal: ac.signal },
        {
          onText: (delta) => patchLast((m) => { ensureText(m).text += delta; }),
          onToolCall: ({ toolCallId, toolName, args }) =>
            patchLast((m) => { m.parts.push({ type: 'tool', toolCallId, toolName, args, state: 'call' }); }),
          onToolResult: ({ toolCallId, result }) =>
            patchLast((m) => {
              const p = m.parts.find((x) => x.type === 'tool' && x.toolCallId === toolCallId);
              if (p) { p.result = result; p.state = 'result'; }
            }),
          onError: (msg) => setError(msg),
        },
      );
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || String(e));
    } finally {
      setStatus('idle');
      abortRef.current = null;
    }
  }, []);

  const reset = React.useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    chatIdRef.current = null; // next message starts a new persisted chat
    setChatId(null);
    setMessages([]);
    setError(null);
    setCacheExpired(false);
    setStatus('idle');
  }, []);

  return { messages, status, error, cacheExpired, clearCacheExpired, chatId, activeProfileId, sendMessage, reset };
}
